import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Input,
  PrimaryButton,
  SecondaryButton,
  Radio,
  INPUT_SIZES,
  Card,
  TextArea,
  RadioGroup,
  withTooltip,
} from "cdk-radial";
import withNavigationToBundles from "./withNavigationToBundles";
import {
  VIEW_BUNDLES,
  PRICING_LIST_PLANS,
  PRICING_ADD_PLAN,
  ADD_A_PLAN,
} from "./screen";
import PricingBreadcrumbs from "./PricingBreadcrumbs";
import { SUB_PLAN_CHANGE } from "../Utils/constants";
import types from "./types";

// table imports
import classNames from "classnames";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import EndpointsListTable from "./EndpointsListTable";

const CreateAContainer = (props) => {
  const {
    auth,
    setScreen,
    setSubPlanContainer,
    setSelectedSubPlanContainer,
    clearSubPlanContainer,
    clearDraftSubPlanState,
    resetBundlePricingPlans,
    resetSelectedPricingPlan,
    resetBundlePlan,
    ApiBundleManagementReducer,
  } = props;

  const {
    selectedSubPlan = null,
    bundlePlan = {},
    selectedBundle = null,
    bundleApiEndpoints = {},
    bundleApiChannels = {},
    subPlanContainers = {},
    selectedSubPlanContainer = null,
  } = ApiBundleManagementReducer;

  const isEdit = selectedSubPlanContainer !== null;
  const plan = bundlePlan[selectedSubPlan] || {};
  const {
    title = "",
    baseValue = "",
    description = "",
    radioValue = "nobucket",
    containers = {},
  } = plan;

  let quotaLimit = "";
  let overages = "";

  const [containerName, setContainerName] = useState("");
  const [isContainerNameValid, setIsContainerNameValid] = useState(true);
  const [containerNameErrorMessage, setContainerNameErrorMessage] = useState("");

  const [containerDescription, setContainerDescription] = useState("");
  const [isContainerDescriptionValid, setIsContainerDescriptionValid] =
    useState(true);

  const [containerQuotaLimit, setContainerQuotaLimit] = useState("");
  const [isContainerQuotaLimitValid, setIsContainerQuotaLimitValid] =
    useState(true);

  const [containerOverages, setContainerOverages] = useState("");
  const [isContainerOveragesValid, setIsContainerOveragesValid] =
    useState(true);
  const [containerEndpoints, setContainerEndpoints] = useState([]);
  const [containerChannels, setContainerChannels] = useState([]);
  const [userSelectedEndpoints, setUserSelectedEndpoints] = useState([]);
  const [userSelectedChannels, setUserSelectedChannels] = useState([]);

  const isCommonFieldsMissing = containerName.trim() === "" || containerDescription.trim() === "";
  const isQuotaOveragesMissing = radioValue === "bucket" && (
    containerQuotaLimit === "" ||
    isNaN(Number(containerQuotaLimit)) ||
    Number(containerQuotaLimit) < 0 ||
    containerOverages === "" ||
    isNaN(Number(containerOverages)) ||
    Number(containerOverages) < 0
  );
  const isMissingEndpoints = userSelectedEndpoints.length === 0 && userSelectedChannels.length === 0;

  const isSaveDisabled =
    isCommonFieldsMissing ||
    !isContainerNameValid ||
    isQuotaOveragesMissing ||
    isMissingEndpoints;

  let tooltipText = "";
  if (!isContainerNameValid) {
    tooltipText = "Container name is already in use";
  } else if (isCommonFieldsMissing || isQuotaOveragesMissing) {
    tooltipText = "Enter all the required fields";
  } else if (isMissingEndpoints) {
    tooltipText = "Select at least one endpoint or channel";
  }

  const breadcrumbs = [
    {
      label: "Bundle Management",
      onClick: () => {
        setScreen(VIEW_BUNDLES);
        clearSubPlanContainer();
        clearDraftSubPlanState();
        resetBundlePricingPlans();
        resetSelectedPricingPlan();
        resetBundlePlan();
      },
    },
    {
      label: "Bundle Pricing",
      onClick: () => {
        setScreen(PRICING_LIST_PLANS);
        clearSubPlanContainer();
        clearDraftSubPlanState();
        resetSelectedPricingPlan();
        resetBundlePlan();
      },
    },
    {
      label: "Add a Plan",
      onClick: () => {
        setScreen(PRICING_ADD_PLAN);
        clearSubPlanContainer();
        clearDraftSubPlanState();
      },
    },
    { label: `${title} Plan`, onClick: () => setScreen(ADD_A_PLAN) },
    { label: "Endpoint Container" },
  ];

  // Pre-populate form when editing existing container
  useEffect(() => {
    if (isEdit && selectedSubPlanContainer) {
      const existingContainer = subPlanContainers[selectedSubPlanContainer];
      if (existingContainer) {
        setContainerName(existingContainer.name || "");
        setContainerDescription(existingContainer.description || "");
        setContainerQuotaLimit(existingContainer.quotaLimit || "");
        setContainerOverages(existingContainer.overages || "");

        // Extract endpoint IDs from saved endpoints
        const endpointIds = (existingContainer.endpoints || []).map(ep => ep.endpointId);
        const channelIds = (existingContainer.channels || []).map(ch => ch.id);

        setUserSelectedEndpoints(endpointIds);
        setUserSelectedChannels(channelIds);
      }
    }
  }, [isEdit, selectedSubPlanContainer, subPlanContainers]);

  const onChangeContainerName = (e) => {
    const value = e.target.value;
    if (value.trim() === "") {
      setIsContainerNameValid(false);
    } else {
      setIsContainerNameValid(true);
    }
    // check if name is already in use for sub plan
    // if yes return with error (but allow the current container's original name when editing)
    if (subPlanContainers[value] && value !== selectedSubPlanContainer) {
      setIsContainerNameValid(false);
      setContainerNameErrorMessage("Container name is already in use");
    } else {
      setIsContainerNameValid(true);
      setContainerNameErrorMessage("");
    }
    setContainerName(value);
  };

  const onChangeContainerDescription = (e) => {
    const value = e.target.value;
    if (value.trim() === "") {
      setIsContainerDescriptionValid(false);
    } else {
      setIsContainerDescriptionValid(true);
    }
    setContainerDescription(value);
  };

  const onChangeQuotaLimit = (e) => {
    const value = e.target.value;
    if (value === "" || isNaN(Number(value)) || Number(value) < 0) {
      setIsContainerQuotaLimitValid(false);
    } else {
      setIsContainerQuotaLimitValid(true);
    }
    setContainerQuotaLimit(value);
  };

  const onChangeOverages = (e) => {
    const value = e.target.value;

    if (value.includes(".") && value.split(".")[1].length > 2) {
      return;
    }

    if (value === "" || isNaN(Number(value)) || Number(value) < 0) {
      setIsContainerOveragesValid(false);
    } else {
      setIsContainerOveragesValid(true);
    }
    setContainerOverages(value);
  };

  const onSaveContainer = () => {

    // Build endpoint and channel objects from selected IDs
    const selectedEndpointIds = Array.from(new Set(userSelectedEndpoints));
    const selectedChannelIds = Array.from(new Set(userSelectedChannels));

    const selectedEndpointObjects = {};
    selectedEndpointIds.forEach(id => {
      if (bundleApiEndpoints[id]) {
        selectedEndpointObjects[id] = bundleApiEndpoints[id];
      }
    });

    const selectedChannelObjects = {};
    selectedChannelIds.forEach(id => {
      if (bundleApiChannels[id]) {
        selectedChannelObjects[id] = bundleApiChannels[id];
      }
    });

    const existingContainer = isEdit && selectedSubPlanContainer ? subPlanContainers[selectedSubPlanContainer] : null;

    // Save container details
    setSubPlanContainer({
      id: existingContainer ? existingContainer.id : undefined,
      name: containerName,
      description: containerDescription,
      quotaLimit: containerQuotaLimit,
      overages: containerOverages,
      apiEndpoints: selectedEndpointObjects,
      channels: selectedChannelObjects,
    });
    // Clear selected container after saving
    if (setSelectedSubPlanContainer) {
      setSelectedSubPlanContainer(null);
    }
    setScreen(ADD_A_PLAN);
  };

  const onCancel = () => {
    // Clear selected container when canceling
    if (setSelectedSubPlanContainer) {
      setSelectedSubPlanContainer(null);
    }
    setScreen(ADD_A_PLAN);
  };

  const SaveButtonRenderer = (props) => (
    <span {...props} style={{ display: "inline-block", cursor: isSaveDisabled ? "not-allowed" : "pointer" }}>
      <PrimaryButton
        dataTestId="datatest-save-plan"
        iconColor="primary"
        onClick={onSaveContainer}
        isDisabled={isSaveDisabled}
        text="Save Container"
      />
    </span>
  );
  
  const SaveButtonWithTooltip = tooltipText 
    ? withTooltip(SaveButtonRenderer, { text: tooltipText, placement: "top" })
    : SaveButtonRenderer;

  return (
    <>
      <Card>
        <div className="create-bundle-card">
          <div className="bundle-pricing-title">
            Pricing - {selectedBundle?.name}
          </div>
          <PricingBreadcrumbs breadcrumbs={breadcrumbs} />
          <div className="bundle_pricing_heading">Endpoint Container</div>
          <>
            <div className="input_box">
              <Input
                id="sub-plan-container-input-simple"
                label="Container Name"
                name="sub-plan-container-input-simple"
                dataTestId="sub-plan-container-input-field"
                enableCustomValidation
                hasError={!isContainerNameValid}
                errorMessage={
                  containerNameErrorMessage || "Enter name of the container"
                }
                size={INPUT_SIZES.LARGE}
                maxLength={100}
                helperText="Name of the container"
                isRequired
                onChange={onChangeContainerName}
                value={containerName}
              />
            </div>
            <div className="input_box">
              <TextArea
                enableCustomValidation
                dataTestId="datatest--container-description"
                id="container-description"
                isRequired
                label="Description"
                maxLength="100"
                hasCharacterCount
                hasError={!isContainerDescriptionValid}
                errorMessage="Enter description of container"
                name="container-description"
                onChange={onChangeContainerDescription}
                helperText="Promotional Text for this container"
                value={containerDescription}
              />
            </div>
            <>
              <EndpointsListTable
                {...props}
                userSelectedChannels={userSelectedChannels}
                setUserSelectedChannels={setUserSelectedChannels}
                userSelectedEndpoints={userSelectedEndpoints}
                setUserSelectedEndpoints={setUserSelectedEndpoints}
                apis={[]}
              />
            </>

            <>
              <div className="input_box">
                <Input
                  enableCustomValidation
                  label="Quota Limit"
                  value={containerQuotaLimit}
                  onChange={onChangeQuotaLimit}
                  hasError={!isContainerQuotaLimitValid}
                  errorMessage="Quota Limit should be greater than or equal to 0"
                  helperText="Calls per month"
                  type="number"
                  isRequired
                  onKeyDown={(evt) =>
                    ["e", ".", "E", "+", "-"].includes(evt.key) &&
                    evt.preventDefault()
                  }
                />
              </div>
              <div className="input_box">
                <Input
                  label="Overages"
                  value={containerOverages}
                  onChange={onChangeOverages}
                  enableCustomValidation
                  hasError={!isContainerOveragesValid}
                  errorMessage="Overages should be 0 or more"
                  helperText="USD per extra call"
                  type="number"
                  prefixText="$"
                  isRequired
                  onKeyDown={(evt) =>
                    ["e", "E", "+", "-"].includes(evt.key) &&
                    evt.preventDefault()
                  }
                />
              </div>
            </>

            <div className="sub-plan-buttons">
              <SaveButtonWithTooltip />
              <SecondaryButton
                dataTestId="datatest-cancel-plan"
                iconColor="secondary"
                onClick={onCancel}
                text="Cancel"
              />
            </div>
          </>
        </div>
      </Card>
    </>
  );
};

export default withNavigationToBundles(CreateAContainer);
